'use client';

import * as React from 'react';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { Calendar as CalendarIcon } from 'lucide-react';
import type { DateRange } from 'react-day-picker';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

interface DateRangePickerProps {
  value?: DateRange | null;
  onChange: (range: DateRange | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function DateRangePicker({
  value,
  onChange,
  placeholder = 'Выберите период',
  disabled = false,
  className,
}: DateRangePickerProps) {
  const [clickCount, setClickCount] = React.useState(0);

  const handleSelect = (range: DateRange | undefined) => {
    const newClickCount = clickCount + 1;

    if (newClickCount === 3) {
      // Третий клик - сброс
      onChange(null);
      setClickCount(0);
      return;
    }

    setClickCount(newClickCount);
    onChange(range ?? null);
  };

  // Сброс счётчика при полном выборе диапазона
  React.useEffect(() => {
    if (value?.from && value?.to) {
      setClickCount(2);
    } else if (value?.from && !value?.to) {
      setClickCount(1);
    } else {
      setClickCount(0);
    }
  }, [value?.from, value?.to]);

  const formatRange = () => {
    if (!value?.from) {return null;}

    if (value.to) {
      return `${format(value.from, 'dd.MM.yyyy', { locale: ru })} — ${format(value.to, 'dd.MM.yyyy', { locale: ru })}`;
    }

    return format(value.from, 'dd.MM.yyyy', { locale: ru });
  };

  const displayValue = formatRange();

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          data-empty={!displayValue}
          disabled={disabled}
          className={cn(
            'w-full justify-start text-left font-normal data-[empty=true]:text-muted-foreground',
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {displayValue ?? <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="range"
          selected={value ?? undefined}
          onSelect={handleSelect}
          locale={ru}
          numberOfMonths={2}
          defaultMonth={value?.from ?? undefined}
          initialFocus
        />
        {value && (
          <div className="border-t px-3 py-2">
            <Button
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={() => {
                onChange(null);
                setClickCount(0);
              }}
            >
              Очистить
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
